<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue'
import { FALLBACK_DOWNLOAD_LINKS } from '~/api/download'
import { isChineseLocale } from '~/composables/useAppI18n'

/** 不同站点语言对应的手机视觉素材。 */
const PHONE_FRAME: Record<string, string> = {
  'zh-cn': 'https://resources1.interface003.com/web30/2026/09/04/5f14e2de-68ac-48cd-8b1b-3a29fbdc3617.png',
  zh: 'https://resources1.interface003.com/web30/2026/09/04/5b139530-072b-4001-a33d-5c8e35ce1380.png',
  other: 'https://resources1.interface003.com/web30/2026/09/04/0cc914e7-16c1-4d29-b8ec-acfa97833591.png',
}

const BACKGROUND_IMAGE = 'https://resources1.interface003.com/web30/2026/09/04/873bfd55-53d7-4452-affa-a9fd95e889fa.jpg'
const CLOSE_ICON = 'https://resources1.interface003.com/web30/2026/09/04/c6d9ac10-d27e-4722-a6d7-c15047969a5e.png'
const SUCCESS_ICON = 'https://resources1.interface003.com/web30/2026/09/04/285c9899-ab92-4cf4-81ac-13b8fa9f6eb7.png'
const APP_LOGO = 'https://resources1.interface003.com/web30/2026/08/03/390d0b89-176a-419d-a4c7-e9ecd8b6e47c.png'

const props = withDefaults(
  defineProps<{
    visible: boolean
    title?: string
  }>(),
  { title: '' },
)

const emit = defineEmits<{
  close: []
}>()

const { t, locale } = useAppI18n()
const { data: downloadAppUrls } = await useAppDownloadLinks()

const isChinese = computed(() => isChineseLocale(locale))

/** 二维码与下载页共用下载落地地址，保证两个入口扫码结果一致。 */
const downloadQrUrl = computed(() => {
  const links = downloadAppUrls.value
  return links?.data?.h5down || FALLBACK_DOWNLOAD_LINKS.data.h5down
})

/** 根据当前站点语言选择对应文案的手机视觉素材。 */
const phoneFrameImage = computed(() => {
  const currentLocale = String(locale).toLowerCase()

  if (currentLocale === 'zh-cn') {
    return PHONE_FRAME['zh-cn']
  }

  if (['zh', 'zh-tw', 'zh-hk'].includes(currentLocale)) {
    return PHONE_FRAME.zh
  }

  return PHONE_FRAME.other
})

/** 下载标题文案，语言包通过 {0} 控制高亮标题在句子中的位置。 */
const downloadTitleHtml = computed(() => {
  const titleHtml = `<span class="px-[3px] text-[#CFE1FD]">${props.title}</span>`
  const titleText = formatTemplateText(t('88833', ['{0}']), { '{0}': titleHtml })

  // 语言包暂未包含占位符时保持旧行为，避免高亮标题丢失。
  return titleText.includes(titleHtml) ? titleText : `${titleText} ${titleHtml}`
})

watch(
  () => props.visible,
  (visible) => {
    updateBodyOverflow(visible)
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  updateBodyOverflow(false)
})

/** 关闭弹窗并通知父组件同步状态。 */
function handleClose() {
  emit('close')
}

/** 为 div 交互入口补齐 Enter 与 Space 键盘行为。 */
function handleKeyboardActivate(event: KeyboardEvent) {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  ;(event.currentTarget as HTMLElement).click()
}

/** 根据弹窗状态锁定背景页面滚动。 */
function updateBodyOverflow(visible: boolean) {
  if (!import.meta.client) {
    return
  }

  document.body.style.overflow = visible ? 'hidden' : ''
}

/**
 * 将语言包文案模板按占位符替换成最终展示文案。
 * @param text 文案模板，通常包含占位符。
 * @param replacements 需要替换的占位符和值。
 * @returns 可直接渲染的文案。
 */
function formatTemplateText(text: unknown, replacements: Record<string, string>) {
  const templateText = typeof text === 'string' ? text : ''

  if (!templateText) {
    return ''
  }

  return Object.entries(replacements).reduce(
    (currentText, [placeholder, replacement]) => currentText.replaceAll(placeholder, replacement),
    templateText,
  )
}
</script>

<template>
  <Teleport to="body">
    <Transition name="home-download-dialog">
      <div
        v-if="visible"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-[rgba(0,0,0,0.55)] p-[16px]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="home-download-dialog-title"
        @click.self="handleClose"
      >
        <div
          class="home-download-dialog relative w-[720px] overflow-hidden rounded-[28px]"
          :class="isChinese ? 'h-[404px]' : 'h-[430px]'"
        >
          <CommonImage
            :src="BACKGROUND_IMAGE"
            alt=""
            width="720"
            height="430"
            loading="eager"
            :fallback-src="false"
            class="absolute inset-0 z-0 size-full object-cover"
            aria-hidden="true"
          />

          <div
            class="absolute inset-x-[32px] top-[32px] z-10 flex items-center border-b border-[rgba(227,228,242,0.12)] pb-[16px]"
          >
            <div
              id="home-download-dialog-title"
              class="me-[8px] min-w-0 flex-1 truncate text-[24px] leading-[32px] font-[600] text-white"
            >
              {{ t('82528') }}
            </div>
            <div
              class="flex size-[32px] shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors hover:brightness-110"
              role="button"
              tabindex="0"
              aria-label="Close"
              @click="handleClose"
              @keydown="handleKeyboardActivate"
            >
              <CommonImage :src="CLOSE_ICON" alt="" class="size-[32px]" loading="eager" :fallback-src="false" />
            </div>
          </div>

          <div class="absolute top-[104px] left-[32px] z-10 flex w-[392px] flex-col rtl:right-[32px] rtl:left-auto">
            <div class="flex flex-col">
              <p class="m-0 w-[350px] text-[18px] leading-[26px] font-[600] text-white" v-html="downloadTitleHtml" />
              <div class="mt-[16px] flex flex-col text-[14px] leading-[22px] text-white">
                <p class="m-0 flex items-start">
                  <CommonImage
                    :src="SUCCESS_ICON"
                    alt=""
                    class="me-[8px] mt-[3px] size-[16px] shrink-0"
                    loading="eager"
                    :fallback-src="false"
                  />
                  <span>{{ t('88835') }}</span>
                </p>
                <p class="m-0 mt-[8px] flex items-start">
                  <CommonImage
                    :src="SUCCESS_ICON"
                    alt=""
                    class="me-[8px] mt-[3px] size-[16px] shrink-0"
                    loading="eager"
                    :fallback-src="false"
                  />
                  <span>{{ t('88836') }}</span>
                </p>
              </div>
            </div>

            <div class="mt-[24px] flex w-fit flex-col items-center">
              <span
                class="flex size-[120px] items-center justify-center rounded-[12px] border border-[rgba(11,18,40,0.14)] bg-white p-[8px]"
              >
                <span class="relative size-[104px]">
                  <CommonQrCode v-if="downloadQrUrl" :text="downloadQrUrl" :size="104" class="size-full" />
                  <span v-if="downloadQrUrl" class="absolute inset-0 m-auto size-[26px]">
                    <CommonImage
                      :src="APP_LOGO"
                      alt="WikiBit"
                      class="size-full"
                      loading="eager"
                      :fallback-src="false"
                    />
                  </span>
                </span>
              </span>
              <span class="mt-[8px] flex items-center text-[14px] leading-[22px] text-[#B7B9BE]">
                {{ t('88837') }}
              </span>
            </div>
          </div>

          <div class="pointer-events-none absolute top-[74px] right-[9px] z-10 rtl:right-auto rtl:left-[9px]">
            <CommonImage
              :src="phoneFrameImage"
              alt=""
              width="328"
              height="auto"
              class="size-full object-contain"
              loading="eager"
              :fallback-src="false"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="less">
.home-download-dialog {
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.home-download-dialog-enter-active,
.home-download-dialog-leave-active {
  transition: opacity 180ms ease;
}

.home-download-dialog-enter-from,
.home-download-dialog-leave-to {
  opacity: 0;
}
</style>
